import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {
  Alert,
  Button,
  Divider,
  Grid2 as Grid,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import { useStore } from "@tanstack/react-form";
import { useEffect, useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";

import {
  useGetMailConfigQuery,
  useListMailListsQuery,
} from "api/memberPortal/mailList/mailList.api";
import { MailingListDomains } from "api/memberPortal/mailList/mailList.types";
import { useAppForm } from "components/Forms/AppForm";
import SearchBar from "components/MUI/DataGrid/Detached/SearchBar";
import { pluralize } from "utils/stringFormatting";

import MailListAccordion from "./components/MailList/components/MailListAccordion/MailListAccordion";
import MailListAccordionSkeleton from "./components/MailList/components/MailListAccordion/MailListAccordion.skeleton";
import {
  defaultMailListFilterValues,
  MAILING_LIST_SORT_OPTIONS,
} from "./components/MailList/MailList.schema";

export default function MailList() {
  const location = useLocation();
  const deletedListName = (location.state as { deletedListName?: string })
    ?.deletedListName;
  const [snackbarDismissed, setSnackbarDismissed] = useState(false);

  const { data: mailConfig } = useGetMailConfigQuery();
  const domainOptions = (mailConfig?.domains ?? []).map((d) => ({
    value: d.label,
    label: d.label,
  }));

  const form = useAppForm({
    defaultValues: defaultMailListFilterValues,
  });

  const sortValue = useStore(form.store, (state) => state.values.sort);
  const domainFilter = useStore(form.store, (state) => state.values.domain);
  const searchValue = useStore(form.store, (state) => state.values.search);

  // Config loads async, so seed the filter with the
  // first available domain once config arrives (only if nothing is selected yet).
  useEffect(() => {
    if (!domainFilter && domainOptions.length > 0) {
      form.setFieldValue("domain", domainOptions[0].value);
    }
  }, [domainFilter, domainOptions, form]);

  const {
    data: mailListData,
    isLoading,
    error,
  } = useListMailListsQuery(
    {
      ordering: sortValue,
      domain: domainFilter as MailingListDomains,
      search: searchValue || undefined,
    },
    // Wait for a domain to be selected before querying (avoids an initial
    // unfiltered fetch flashing before the seed runs).
    { skip: !domainFilter },
  );

  return (
    <Stack sx={{ px: 3, my: 5 }} gap={3}>
      <Typography variant="h1">Mailing Lists</Typography>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        <Grid size={4}>
          <SearchBar
            sx={{ width: "100%" }}
            onSearchChange={(value) => form.setFieldValue("search", value)}
            placeholder="Search mail lists..."
          />
        </Grid>
        <Grid sx={{ alignItems: "center" }}>
          <Stack direction="row" justifyContent="space-between" gap={2}>
            <Button variant="outlined" component={Link} to="members">
              Manage Members
            </Button>
            <Button variant="outlined" component={Link} to="categories">
              Manage categories
            </Button>
            <Button
              startIcon={<AddCircleOutlineIcon />}
              variant="contained"
              component={Link}
              to="add"
            >
              New List
            </Button>
          </Stack>
        </Grid>
      </Grid>
      <Grid container alignItems={"center"} justifyContent={"space-between"}>
        <Grid size={6}>
          <Stack
            direction="row"
            justifyContent={"left"}
            alignItems={"center"}
            gap={2}
          >
            <form.AppField name="domain">
              {(field) => (
                <field.ToggleGroupSelect label="" options={domainOptions} />
              )}
            </form.AppField>
            <form.AppField name="sort">
              {(field) => (
                <field.SelectField
                  label="Sort"
                  options={MAILING_LIST_SORT_OPTIONS}
                />
              )}
            </form.AppField>{" "}
          </Stack>
        </Grid>
        <Grid>
          <Typography variant="subtitle2">
            {mailListData
              ? `Showing ${pluralize(mailListData.length ?? 0, "list")}`
              : "Loading..."}
          </Typography>
        </Grid>
      </Grid>

      <Divider />

      {isLoading ? (
        <MailListAccordionSkeleton />
      ) : error ? (
        <Alert severity="error">Failed to load mail lists</Alert>
      ) : !mailListData?.length ? (
        <Typography>No mail lists found</Typography>
      ) : (
        <MailListAccordion data={mailListData} />
      )}
      <Outlet />

      <Snackbar
        open={!!deletedListName && !snackbarDismissed}
        autoHideDuration={5000}
        onClose={() => setSnackbarDismissed(true)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSnackbarDismissed(true)}
        >
          {`Mailing list "${deletedListName}" deleted successfully.`}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
